import { MapStateType, StatisticsMapState } from '@redux/maps/maps.reducer';
import { colors } from '@theme/foundations/colors';
import { featureEach } from '@turf/meta';
import { distance, nearestPointOnLine, polygonToLine } from '@turf/turf';
import { getPopupDataType } from '@utils/popup.utils';
import { getTargetClassification, getTargetIdentification, isDrone, isRemoteControl } from '@utils/target.utils';
import { FeatureCollection, MultiPolygon, Position } from 'geojson';

import { Target } from '@/types/c2/c2.types';
import { Coordinates } from '@/types/commons/commons.types';
import { ContextEnum } from '@/types/config/screenConfiguration.types';
import { BaseStyleRaster, LayerNameEnum, MapIdEnum } from '@/types/map.types';
import { InfoDataTypeEnum, PopupType, TargetInfoData } from '@/types/popup.types';
import { ClassificationEnum } from '@/types/sensor/classification.types';
import { AbstractLocation, TargetLocation } from '@/types/sensor/sensor.types';
import { AbstractFlightPlan, FlightPlanStateEnum } from '@/types/utm/flightPlan.types';

import { flightPlanBorderColor, orientedTarget, targetColor } from './map.constants';

export function isBaseStyleRaster(object: unknown): object is BaseStyleRaster {
  return typeof object === 'object' && object !== null && 'baseStyle' in object && 'url' in object;
}

export function isFeatureCollection(data: unknown): data is FeatureCollection {
  return typeof data === 'object' && data !== null && 'type' in data && 'features' in data;
}

export function getTargetColor(target: Target<TargetLocation>, organization: string | null): string {
  if (isDrone(target, organization) || isRemoteControl(target, organization)) {
    return targetColor[getTargetClassification(target, organization)];
  }
  return targetColor[ClassificationEnum.UNCLASSIFIED];
}

export function getAvailableLayers(map: mapboxgl.Map, layers: LayerNameEnum | LayerNameEnum[]): LayerNameEnum[] {
  const availableLayers = map.getStyle().layers.map((layer) => layer.id);

  if (Array.isArray(layers)) {
    return layers.filter((layer) => availableLayers.includes(layer));
  } else {
    return availableLayers.includes(layers) ? [layers] : [];
  }
}

export function getMapIdFromContext(context: ContextEnum): MapIdEnum {
  switch (context) {
    case ContextEnum.REPLAY:
      return MapIdEnum.REPLAY;
    case ContextEnum.DOUBT_LAD:
      return MapIdEnum.LAD;
    case ContextEnum.LIVE:
    default:
      return MapIdEnum.LIVE;
  }
}

export function getMapIdFromReplayMode(replayMode: boolean, currentContext: ContextEnum): MapIdEnum {
  return replayMode ? MapIdEnum.REPLAY : getMapIdFromContext(currentContext);
}

export function shouldHideTooltip(targetInfoPopup: PopupType | null, target: Target<TargetLocation>): boolean {
  if (!targetInfoPopup) {
    return false;
  }

  return (
    getPopupDataType(targetInfoPopup) === InfoDataTypeEnum.INFO_TARGET &&
    target.id === (targetInfoPopup.data as TargetInfoData | null)?.targetId
  );
}

export function getClosestPointOnMultiPolygon(point: Position, multiPolygon: MultiPolygon): Position {
  const multiPolygonLines = polygonToLine(multiPolygon);

  let closestPoint: Position = point;
  let minDistance = Infinity;

  featureEach(multiPolygonLines, (feature) => {
    const line = feature.geometry;
    if (line.type === 'LineString') {
      const candidatePoint = nearestPointOnLine(feature, point);
      const dist = distance(point, candidatePoint);

      if (dist < minDistance) {
        minDistance = dist;
        closestPoint = candidatePoint.geometry.coordinates;
      }
    }
  });

  return closestPoint;
}

export function getBarycenter(coordinates: Coordinates[]): Coordinates {
  return coordinates.reduce(
    (acc, p, _, a) => ({
      latitude: acc.latitude + p.latitude / a.length,
      longitude: acc.longitude + p.longitude / a.length,
      type: '2D',
    }),
    { latitude: 0, longitude: 0, type: '2D' },
  );
}

export function isStatisticsMap(map: MapStateType): map is StatisticsMapState {
  return map.mapId === MapIdEnum.STATISTICS;
}

export function isOrientedTarget(target: Target<AbstractLocation>, organization: string | null) {
  return orientedTarget.includes(getTargetIdentification(target, organization));
}

const SESSION_SEED = Math.floor(Math.random() * 100000);
export function getColorByDisplayId(id: string | null, seed = SESSION_SEED): string {
  if (!id) return colors.neutral[500].value;

  let hash = seed;
  for (let i = 0; i < id.length; i++) {
    hash = (id.codePointAt(i) ?? 0) + ((hash << 5) - hash);
  }
  const color = (hash & 0x00ffffff).toString(16).toUpperCase();

  return `#${'000000'.substring(0, 6 - color.length)}${color}`;
}

export function getFlightPlanBorderColor(flightPlan: AbstractFlightPlan) {
  return flightPlanBorderColor[
    flightPlan.state === FlightPlanStateEnum.ACTIVE && flightPlan.linkedDroneDetection !== null
      ? flightPlan.linkedDroneDetection
      : FlightPlanStateEnum.INACTIVE
  ];
}
